import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import SalesManage, { type SalesManageSaveReason } from '../SalesManage';
import type { Invoice } from '../../types/invoice';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';
import {
  parseInventoryHubLocationState,
  resolveInvoiceEditCloseTarget,
} from './inventoryHubNavigation';

const InventoryInvoiceEditOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => parseInventoryHubLocationState(location.state),
    [location.state]
  );

  const isNew = id === 'new';
  const parsedId = id != null ? Number(id) : Number.NaN;
  const invoiceId = isNew || Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    const target = resolveInvoiceEditCloseTarget(invoiceId, listSearch, navState);
    navigate(target, { preventScrollReset: true });
  }, [invoiceId, listSearch, navState, navigate]);

  const handleInvoiceSaved = useCallback(
    (invoice: Invoice, reason: SalesManageSaveReason) => {
      refetchList();

      if (reason === 'draft_created') {
        navigate(
          {
            pathname: `/inventory-transactions/invoice/${invoice.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
        return;
      }

      if (reason === 'posted' || reason === 'amended') {
        navigate(
          {
            pathname: `/inventory-transactions/view/invoice/${invoice.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
      }
    },
    [close, listSearch, location.state, navigate, refetchList]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <SalesManage
        key={invoiceId ?? 'new'}
        embedded
        invoiceId={invoiceId}
        onClose={close}
        onInvoiceSaved={handleInvoiceSaved}
      />
    </TransactionOverlay>
  );
};

export default InventoryInvoiceEditOverlay;
